// src/lib/session-utils.ts
import { supabase } from "./supabase";

// Interface for user session data
export interface UserSession {
  userId: string;
  token: string;
  expiresAt: number;
}

// Register with service worker to ensure it's ready
let serviceWorkerRegistration: ServiceWorkerRegistration | null = null;

// Initialize the client connection to the service worker
export const initServiceWorker = async (): Promise<boolean> => {
  if (!("serviceWorker" in navigator)) {
    console.warn("Service workers are not supported in this browser");
    return false;
  }

  try {
    // Wait for the service worker to be ready
    serviceWorkerRegistration = await navigator.serviceWorker.ready;

    // Register message event listener
    setupServiceWorkerMessageHandling();

    console.log("Service worker client initialized successfully");
    return true;
  } catch (error) {
    console.error("Error initializing service worker client:", error);
    return false;
  }
};

// Set up event listeners for service worker messages
const setupServiceWorkerMessageHandling = () => {
  navigator.serviceWorker.addEventListener(
    "message",
    handleServiceWorkerMessage
  );
};

// Store for callbacks that are waiting for service worker responses
const messageCallbacks: Record<string, (data: any) => void> = {};

// Generate a unique message ID
const generateMessageId = (): string => {
  return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Send a message to the service worker and return a promise for the response
export const sendMessageToServiceWorker = async <T>(
  message: any,
  timeout = 5000
): Promise<T> => {
  if (!navigator.serviceWorker.controller) {
    throw new Error("Service worker not active");
  }

  // Add a unique message ID for tracking the response
  const messageId = generateMessageId();
  const messageWithId = { ...message, messageId };

  // Create a promise that will resolve when we get a response with this ID
  const responsePromise = new Promise<T>((resolve, reject) => {
    // Set a timeout to reject the promise if we don't get a response
    const timeoutId = setTimeout(() => {
      delete messageCallbacks[messageId];
      reject(new Error("Service worker response timeout"));
    }, timeout);

    // Store the callback
    messageCallbacks[messageId] = (data) => {
      clearTimeout(timeoutId);
      resolve(data as T);
    };
  });

  // Send the message to the service worker
  navigator.serviceWorker.controller.postMessage(messageWithId);

  return responsePromise;
};

// Handle messages from the service worker
const handleServiceWorkerMessage = (event: MessageEvent) => {
  const { type, messageId, ...data } = event.data;

  // If this is a response to a specific message, call the stored callback
  if (messageId && messageCallbacks[messageId]) {
    messageCallbacks[messageId](data);
    delete messageCallbacks[messageId];
    return;
  }

  // Otherwise, handle based on message type
  switch (type) {
    case "SESSION_STATUS":
      // Handle session status updates
      if (data.valid && window.sessionStorage) {
        window.sessionStorage.setItem("userSessionValid", "true");
      } else if (window.sessionStorage) {
        window.sessionStorage.removeItem("userSessionValid");
      }
      break;

    case "PERMISSION_STATUS":
      // Handle permission status updates
      if (data.permissionType && data.status && window.localStorage) {
        window.localStorage.setItem(
          `permission_${data.permissionType}`,
          data.status
        );
      }
      break;

    case "UPLOAD_PROGRESS":
    case "UPLOAD_COMPLETED":
    case "UPLOAD_FAILED":
      // These events are already handled by your existing upload context
      break;

    default:
      console.log("Unhandled service worker message:", type, data);
  }
};

// Check if the user has an existing session
export const checkUserSession = async (): Promise<boolean> => {
  try {
    // First check if we have the user in Supabase
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      return false;
    }

    // Then check if we have a valid session in the service worker
    if (navigator.serviceWorker.controller) {
      try {
        const response = await sendMessageToServiceWorker<{ valid: boolean }>({
          type: "CHECK_SESSION",
          userId: user.id,
        });

        return response.valid;
      } catch (error) {
        console.error("Error checking session with service worker:", error);
        return false;
      }
    }

    // If no service worker, check browser storage as fallback
    if (window.sessionStorage) {
      return window.sessionStorage.getItem("userSessionValid") === "true";
    }

    return false;
  } catch (error) {
    console.error("Error checking user session:", error);
    return false;
  }
};

// Create a new user session
export const createUserSession = async (): Promise<boolean> => {
  try {
    // Get current user and session
    const {
      data: { user },
    } = await supabase.auth.getUser();
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!user || !session) {
      return false;
    }

    // Create session in service worker
    if (navigator.serviceWorker.controller) {
      try {
        await sendMessageToServiceWorker({
          type: "CREATE_SESSION",
          userId: user.id,
          token: session.access_token,
        });

        // Also store in browser storage as backup
        if (window.sessionStorage) {
          window.sessionStorage.setItem("userSessionValid", "true");
        }

        return true;
      } catch (error) {
        console.error("Error creating session with service worker:", error);

        // Fallback to browser storage
        if (window.sessionStorage) {
          window.sessionStorage.setItem("userSessionValid", "true");
          return true;
        }

        return false;
      }
    }

    // If no service worker, use browser storage
    if (window.sessionStorage) {
      window.sessionStorage.setItem("userSessionValid", "true");
      return true;
    }

    return false;
  } catch (error) {
    console.error("Error creating user session:", error);
    return false;
  }
};

// Save user permission preference
export const saveUserPermission = async (
  permissionType: string,
  status: string
): Promise<boolean> => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      return false;
    }

    // Save in service worker
    if (navigator.serviceWorker.controller) {
      try {
        await sendMessageToServiceWorker({
          type: "SAVE_PERMISSION",
          userId: user.id,
          permissionType,
          status,
        });
      } catch (error) {
        console.error("Error saving permission with service worker:", error);
      }
    }

    // Also save in localStorage as backup
    if (window.localStorage) {
      window.localStorage.setItem(`permission_${permissionType}`, status);
    }

    return true;
  } catch (error) {
    console.error("Error saving user permission:", error);
    return false;
  }
};

// Get user permission preference
export const getUserPermission = async (
  permissionType: string
): Promise<string | null> => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      return null;
    }

    // First try to get from service worker
    if (navigator.serviceWorker.controller) {
      try {
        const response = await sendMessageToServiceWorker<{
          status: string | null;
        }>({
          type: "GET_PERMISSION",
          userId: user.id,
          permissionType,
        });

        if (response.status) {
          return response.status;
        }
      } catch (error) {
        console.error("Error getting permission from service worker:", error);
      }
    }

    // Fallback to localStorage
    if (window.localStorage) {
      const status = window.localStorage.getItem(
        `permission_${permissionType}`
      );
      return status;
    }

    return null;
  } catch (error) {
    console.error("Error getting user permission:", error);
    return null;
  }
};

// Ask for location permission and save the user's choice
export const requestLocationPermission = async (): Promise<boolean> => {
  try {
    // First check if we already have a saved preference
    const savedPreference = await getUserPermission("geolocation");

    if (savedPreference === "granted") {
      // User previously granted permission, try to get location
      return await requestGeolocation();
    } else if (savedPreference === "denied") {
      // User previously denied permission, respect their choice
      return false;
    }

    // No saved preference, ask for permission
    const result = await requestGeolocation();

    // Save the result
    await saveUserPermission("geolocation", result ? "granted" : "denied");

    return result;
  } catch (error) {
    console.error("Error requesting location permission:", error);
    return false;
  }
};

// Helper function to request geolocation
const requestGeolocation = (): Promise<boolean> => {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      () => resolve(true), // Success
      () => resolve(false), // Error
      { timeout: 10000 } // Options
    );
  });
};

// Resume any pending upload tasks
export const resumePendingUploads = async (): Promise<boolean> => {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      return false;
    }

    if (navigator.serviceWorker.controller) {
      await sendMessageToServiceWorker({
        type: "RESUME_PENDING_TASKS",
        userId: user.id,
      });

      return true;
    }

    return false;
  } catch (error) {
    console.error("Error resuming pending uploads:", error);
    return false;
  }
};
